import {css, StyleSheet} from 'aphrodite';
import moment from 'moment';
import React, {useEffect, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {isInaccessibleInfo} from 'auth/RequestAccess';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import {ProcessClientOrigin, ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import SchemaTagTableSchemaSelector from 'metadata/SchemaTagTableSchemaSelector';
import TagTable from 'metadata/TagTable';
import {calcTagTableErrorsByFieldID, useTagTableState} from 'metadata/useTagTableState';
import {ItemListingQuery} from 'nav/sidepanel/listing/__generated/ItemListingQuery';
import {EntrySummary} from 'notebook/ELNConstants';
import EntrySearchInput from 'notebook/entries/EntrySearchInput';
import clampEntryName from 'notebook/entry-templates/clampEntryName';
import {
  CreateEntryFromTemplateQuery,
  CreateEntryFromTemplateQueryVariables,
} from 'notebook/entry-templates/CreateEntryFromTemplateModal/__generated/CreateEntryFromTemplateQuery';
import {SCHEMA_AND_TAGS_FROM_TEMPLATE_QUERY} from 'notebook/entry-templates/CreateEntryFromTemplateModal/CreateEntryFromTemplateModal';
import ReviewConstants from 'notebook/reviews/ReviewConstants';
import {ExecutionParameters, isExecuteIntoNewEntryParameters} from 'processes/dao/ProcessManagementDAO';
import {isExecutionBlockedByTemplatePublishing} from 'processes/util/ProcessTaskExecutionUtil';
import * as ComputedTagUtil from 'schemas/tags/computed/ComputedTagUtil';
import * as IntegrationTagUtil from 'schemas/tags/integration/IntegrationTagUtil';
import {getSortedSchemaAndCustomTags, getTagsToSet, getTagTableFriendlyTags} from 'schemas/tags/TagUtil';
import {FilterKey, keyToEmptyFilter, SearchContext} from 'search/SearchConstants';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import {ButtonSize} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import DateField from 'ui-library/inputs/DateField';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {MOMENT_DATE_FORMAT} from 'ui-library/inputs/LegacyDateInput';
import {ExpandableModal} from 'ui-library/modals/ExpandableModal';
import ToggleGroup from 'ui-library/ToggleGroup';
import APIUtils from 'util/APIUtil';
import {USER_EDIT_DATE_FORMAT} from 'util/DateConstants';
import * as DateUtils from 'util/DateUtil';

export enum ExecutionType {
  CREATE_NEW_ENTRY = 'CREATE_NEW_ENTRY',
  USE_EXISTING_ENTRY = 'USE_EXISTING_ENTRY',
}

interface ExecuteIntoNewEntryModalParameters {
  type: ExecutionType.CREATE_NEW_ENTRY;
  entryName: string;
  folderId: string | null;
  initialDay: string;
  actionOrigin?: ActionOrigin;
}

interface ExecuteIntoNewEntryModalProps {
  taskIds: Array<string>;
  entryTemplate: {
    id: string;
    name: string;
  };
  entryFolderId: string | null;
  onParameterUpdate: (args: ExecuteIntoNewEntryModalParameters & {areParametersInvalid: boolean}) => void;
  actionOrigin?: ActionOrigin;
  clientOrigin?: ProcessClientOrigin;
}

function ExecuteIntoNewEntryModal(props: ExecuteIntoNewEntryModalProps): JSX.Element {
  const [entryName, setEntryName] = useState(props.entryTemplate.name);
  const [initialDay, setInitialDay] = useState(moment().format(MOMENT_DATE_FORMAT));
  const [folderId, setFolderId] = useState(props.entryFolderId);
  useEffect(() => {
    const areParametersInvalid =
      entryName.trim().length === 0 ||
      initialDay.length === 0 ||
      !DateUtils.isValidDateOrDatetimeOrTime({
        textValue: initialDay,
        validFormats: [USER_EDIT_DATE_FORMAT],
      }) ||
      folderId == null ||
      !APIUtils.isFolder(folderId);
    props.onParameterUpdate({
      type: ExecutionType.CREATE_NEW_ENTRY,
      entryName,
      initialDay,
      folderId,
      actionOrigin: props.actionOrigin,
      areParametersInvalid,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryName, initialDay, folderId]);

  return (
    <>
      <Row>
        <Col sm={7}>
          <FormGroup dataTestAttrs={{component: 'ExecuteTasksModal', element: 'template'}}>
            <FieldLabel label="Template" />
            <p>{props.entryTemplate.name}</p>
          </FormGroup>
        </Col>
        <Col sm={5}>
          <FormGroup dataTestAttrs={{component: 'ExecuteTasksModal', element: 'initialDay'}}>
            <FieldLabel label="Initial Day" isRequired={true} />
            <DateField
              labelStyle={DateField.LABEL_STYLES.HIDDEN}
              value={initialDay}
              onChange={(newDay: string) => {
                setInitialDay(newDay);
                analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.UPDATED_EXECUTION_ENTRY_DATE, {
                  process_client_origin: props.clientOrigin,
                  old_value: initialDay,
                  new_value: newDay,
                });
              }}
              placeholder=""
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col sm={7}>
          <FormGroup dataTestAttrs={{component: 'ExecuteTasksModal', element: 'name'}}>
            <FieldLabel label="Name" isRequired={true} />
            <input
              type="text"
              value={entryName}
              onChange={(e) => {
                setEntryName(clampEntryName(e.target.value));
              }}
            />
          </FormGroup>
        </Col>
        <Col sm={5}>
          <FormGroup dataTestAttrs={{component: 'ExecuteTasksModal', element: 'folder'}}>
            <FieldLabel label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} isRequired={true} />
            <FolderSelector
              enableRedesign={true}
              showNotebook={true}
              value={folderId}
              onChange={(e) => {
                const selectedFolderId = e.target.value;
                setFolderId(selectedFolderId);
                analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.UPDATED_EXECUTION_ENTRY_FOLDER, {
                  process_client_origin: props.clientOrigin,
                  old_value: folderId,
                  new_value: selectedFolderId,
                });
              }}
              unsetOnFetchFail={true}
            />
          </FormGroup>
        </Col>
      </Row>
    </>
  );
}

export interface ExecuteIntoExistingEntryModalParameters {
  type: ExecutionType.USE_EXISTING_ENTRY;
  entryId: string | null;
  actionOrigin?: ActionOrigin;
}

interface ExecuteIntoExistingEntryModalProps {
  taskIds: Array<string>;
  entryTemplate: {
    id: string;
    name: string;
  };
  onParameterUpdate: (
    args: ExecuteIntoExistingEntryModalParameters & {areParametersInvalid: boolean}
  ) => void;
  actionOrigin?: ActionOrigin;
  clientOrign?: ProcessClientOrigin;
}

function ExecuteIntoExistingEntryModal(props: ExecuteIntoExistingEntryModalProps): JSX.Element {
  const [entry, setEntry] = useState<EntrySummary | null>(null);

  useEffect(() => {
    const areParametersInvalid = entry == null;
    props.onParameterUpdate({
      type: ExecutionType.USE_EXISTING_ENTRY,
      entryId: entry?.id ?? null,
      actionOrigin: props.actionOrigin,
      areParametersInvalid,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry]);

  return (
    <Row>
      <Col sm={5}>
        <FormGroup dataTestAttrs={{component: 'ExecuteTasksModal', element: 'template'}}>
          <FieldLabel label="Template" />
          <p>{props.entryTemplate.name}</p>
        </FormGroup>
      </Col>
      <Col sm={7}>
        <FormGroup dataTestAttrs={{component: 'ExecuteTasksModal', element: 'entrySelector'}}>
          <FieldLabel label="Entry" isRequired={true} />
          <EntrySearchInput
            placeholder="Select entry"
            selectedEntries={entry != null ? [entry] : []}
            onChange={(entrySummaries: Array<EntrySummary>) => {
              setEntry(entrySummaries?.[0] ?? null);
            }}
            shouldAllowMultiple={false}
            context={SearchContext.FOR_WRITE_ACTION}
            additionalFilters={[
              {...keyToEmptyFilter(FilterKey.ENTRY_TEMPLATE), value: [props.entryTemplate.id]},
              {
                ...keyToEmptyFilter(FilterKey.REVIEW),
                key: FilterKey.REVIEW,
                value: {
                  review_status: ReviewConstants.IN_PROGRESS_FILTER,
                  role: null,
                },
              },
            ]}
          />
        </FormGroup>
      </Col>
    </Row>
  );
}
type TaskSchemaEntryTemplate = ExtractByTypename<
  ExtractByTypename<ItemListingQuery['processTasks'][number], 'ProcessTaskNode'>['schema']['entryTemplate'],
  'TemplateNode'
>;

export interface ExecuteTasksModalProps {
  taskIds: Array<string>;
  entryTemplate: TaskSchemaEntryTemplate;
  entryFolderId: string | null;
  onRequestClose: () => void;
  onRequestSubmit: (executionParameters: ExecutionParameters) => void;
  actionOrigin?: ActionOrigin;
  clientOrigin?: ProcessClientOrigin;
}

export default function ExecuteTasksModal(props: ExecuteTasksModalProps): JSX.Element {
  const noEffectiveTemplate = isExecutionBlockedByTemplatePublishing({
    executionType: ProcessExecutionType.ENTRY,
    entryTemplate: props.entryTemplate,
  });

  const [executionType, setExecutionType] = useState(
    noEffectiveTemplate ? ExecutionType.USE_EXISTING_ENTRY : ExecutionType.CREATE_NEW_ENTRY
  );
  const [areParametersInvalid, setAreParametersInvalid] = useState(true);
  const [executionParams, setExecutionParams] = useState<
    ExecuteIntoNewEntryModalParameters | ExecuteIntoExistingEntryModalParameters
  >();
  const {tagSchema, schemaTags, updateTagSchema, updateSchemaTag} = useTagTableState();
  const {entryFolderId, ...propsForExistingEntryModal} = props;

  const result = useBenchlingQuery<CreateEntryFromTemplateQuery, CreateEntryFromTemplateQueryVariables>(
    SCHEMA_AND_TAGS_FROM_TEMPLATE_QUERY,
    {
      variables: {
        id: props.entryTemplate.id,
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const _data: CreateEntryFromTemplateQuery | null =
    result.type === QueryResultType.SUCCESS && !isInaccessibleInfo(result.data) ? result.data : null;

  useEffect(() => {
    if (result.type === QueryResultType.SUCCESS && _data) {
      const template = _data.template;
      if (!isInaccessible(template) && template.tagSchema != null) {
        const _tagSchema = graphQLToREST('TagSchema', template.tagSchema);
        const {schemaTags} = getSortedSchemaAndCustomTags({
          tags: template.tagsJson,
          tagSchema: _tagSchema,
        });
        updateTagSchema(_tagSchema, schemaTags);
      } else {
        updateTagSchema(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.type, _data]);

  // renders the tag table or a loading indicator if the query is pending
  const renderTagTable = (): JSX.Element =>
    result.type !== QueryResultType.PENDING ? (
      <TagTable
        canRefreshTags={
          ComputedTagUtil.hasComputedTags(getTagTableFriendlyTags(schemaTags)) ||
          IntegrationTagUtil.hasIntegrationTags(getTagTableFriendlyTags(schemaTags))
        }
        emptyMessage="This schema has no fields."
        errorsByFieldId={errorsByFieldId}
        tags={schemaTags}
        areTagsEditable={false}
        isLoadingTags={false}
        sequenceParts={null}
        subheaderNode={
          <SchemaTagTableSchemaSelector
            fileType={ModelTypeConstants.ENTRY}
            isSchemaEditable={true}
            isFileRegistered={false}
            onUpdateTagSchema={updateTagSchema}
            tagSchema={tagSchema}
          />
        }
        title={<div>Schema</div>}
        onUpdateTag={updateSchemaTag}
        isEditing={true}
      />
    ) : (
      <CenteredHorizontalThrobber />
    );

  const getModalBody = (): JSX.Element | null => {
    switch (executionType) {
      case ExecutionType.CREATE_NEW_ENTRY:
        return (
          <ExecuteIntoNewEntryModal
            {...props}
            onParameterUpdate={({areParametersInvalid, ...executionParameters}) => {
              setAreParametersInvalid(areParametersInvalid);
              setExecutionParams(executionParameters);
            }}
          />
        );
      case ExecutionType.USE_EXISTING_ENTRY:
        return (
          <ExecuteIntoExistingEntryModal
            {...propsForExistingEntryModal}
            onParameterUpdate={({areParametersInvalid, ...executionParameters}) => {
              setAreParametersInvalid(areParametersInvalid);
              setExecutionParams(executionParameters);
            }}
          />
        );
      default:
        assert(false, `ExecuteTasksModal has not been configured for ${executionType} execution type`);
        return null;
    }
  };
  const errorsByFieldId = tagSchema ? calcTagTableErrorsByFieldID(tagSchema, schemaTags) : {};
  return (
    <ExpandableModal
      title="Execute tasks"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        assert(executionParams != null, 'Execution parameters should not be null');
        let paramsToSubmit: ExecutionParameters;
        if (executionParams.type === ExecutionType.CREATE_NEW_ENTRY && executionParams.folderId != null) {
          paramsToSubmit = {
            taskIds: props.taskIds,
            folderId: executionParams.folderId,
            entryName: executionParams.entryName,
            initialDay: executionParams.initialDay,
            actionOrigin: executionParams.actionOrigin,
          };
        } else if (
          executionParams.type === ExecutionType.USE_EXISTING_ENTRY &&
          executionParams.entryId != null
        ) {
          paramsToSubmit = {
            taskIds: props.taskIds,
            entryId: executionParams.entryId,
            actionOrigin: executionParams.actionOrigin,
          };
        } else {
          assert(false, 'Unexpected execution parameters', {executionParams});
        }
        props.onRequestSubmit({
          ...paramsToSubmit,
          schemaTags: getTagsToSet(schemaTags),
          tagSchemaId: tagSchema?.id,
        });
        if (
          isExecuteIntoNewEntryParameters(paramsToSubmit) &&
          paramsToSubmit.entryName !== props.entryTemplate.name
        ) {
          analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.UPDATED_EXECUTION_ENTRY_NAME, {
            process_client_origin: props.clientOrigin,
            old_value: props.entryTemplate.name,
            new_value: paramsToSubmit.entryName,
          });
        }
      }}
      buttons={[
        {
          text: 'Execute',
          disabled:
            areParametersInvalid ||
            executionParams == null ||
            // Disable if schema is required but not present
            (executionType === ExecutionType.CREATE_NEW_ENTRY &&
              BenchlingConfig.ELN_ENABLE_SCHEMA_REQUIRED_FOR_REVIEW &&
              BenchlingConfig.ELN_ENABLE_SCHEMA_ON_ENTRY_CREATE_MODAL &&
              tagSchema == null) ||
            // Disable if there are schema related errors and schema is being used (ie create new entry) and
            // required fields are required on create
            (executionType === ExecutionType.CREATE_NEW_ENTRY &&
              BenchlingConfig.ELN_ENABLE_SCHEMA_ON_ENTRY_CREATE_MODAL &&
              BenchlingConfig.ELN_ENFORCE_REQUIRED_FIELDS_ON_ENTRY_CREATE &&
              Object.keys(errorsByFieldId).length > 0),
          submit: true,
        },
      ]}
      modalKey="executeTasks"
      rememberExpandedSetting={true}
    >
      <Row>
        <Col sm={7}>
          <ToggleGroup
            selection={executionType}
            size={ButtonSize.SMALL}
            isFullWidth={false}
            onChangeSelection={(selection) => {
              setExecutionType(selection);
            }}
            options={[
              {
                id: ExecutionType.CREATE_NEW_ENTRY,
                buttonProps: {
                  text: 'Create new entry',
                },
                dataTestAttrs: {component: 'ExecuteTasksModal', element: 'createNewEntryOption'},
                disabled: noEffectiveTemplate,
                popOverProps: {
                  disabled: !noEffectiveTemplate,
                  text: "A new entry can't be created because the execution template is unavailable. Use an existing entry, or contact your admin for assistance.",
                },
              },
              {
                id: ExecutionType.USE_EXISTING_ENTRY,
                buttonProps: {
                  text: 'Use existing entry',
                },
                dataTestAttrs: {component: 'ExecuteTasksModal', element: 'executeIntoExistingEntryOption'},
              },
            ]}
          />
        </Col>
      </Row>
      {getModalBody()}
      {BenchlingConfig.ELN_ENABLE_SCHEMA_ON_ENTRY_CREATE_MODAL &&
        executionType === ExecutionType.CREATE_NEW_ENTRY && (
          <Row className={css(styles.entrySchema)}>
            <FieldLabel
              label="Entry Schema"
              isRequired={BenchlingConfig.ELN_ENABLE_SCHEMA_REQUIRED_FOR_REVIEW}
            />
            {renderTagTable()}
          </Row>
        )}
    </ExpandableModal>
  );
}

const styles = StyleSheet.create({
  entrySchema: {
    padding: '0 15px',
  },
});
